import {
	useBlockProps,
	useInnerBlocksProps,
	store as blockEditorStore,
} from '@wordpress/block-editor';
import { Button } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import { useSelect } from '@wordpress/data';

import Settings from './Settings';
import { TEMPLATE } from '../constants';

import '../style.scss';
import '../editor.scss';

export default function Edit( { attributes, setAttributes, clientId } ) {
	const {
		orientation,
		tabPosition,
		tabAlignment,
		tabWidth,
		activeTabColor,
		activeTabBg,
		hoverTabColor,
		hoverTabBg,
		inactiveTabBg,
		inactiveTabColor,
		tabGap,
		contentPadding,
	} = attributes;

	const [ activeTab, setActiveTab ] = useState( 0 );

	// Read children's label attributes
	const children = useSelect(
		( select ) => select( blockEditorStore ).getBlocks( clientId ),
		[ clientId ]
	);

	// Auto-switch when a child Tab (or descendant) is selected
	const selectedTabIndex = useSelect(
		( select ) => {
			const { getSelectedBlockClientId, getBlockParents, getBlocks } =
				select( blockEditorStore );
			const selectedId = getSelectedBlockClientId();
			if ( ! selectedId ) {
				return null;
			}

			const innerBlocks = getBlocks( clientId );
			const parents = getBlockParents( selectedId, true );

			for ( let i = 0; i < innerBlocks.length; i++ ) {
				const childId = innerBlocks[ i ].clientId;
				if (
					selectedId === childId ||
					parents.includes( childId )
				) {
					return i;
				}
			}
			return null;
		},
		[ clientId ]
	);

	useEffect( () => {
		if ( selectedTabIndex !== null ) {
			setActiveTab( selectedTabIndex );
		}
	}, [ selectedTabIndex ] );

	// Build CSS vars
	const paddingVars = {};
	if ( contentPadding && typeof contentPadding === 'object' ) {
		if ( contentPadding.top )
			paddingVars[ '--content-padding-top' ] = contentPadding.top;
		if ( contentPadding.right )
			paddingVars[ '--content-padding-right' ] =
				contentPadding.right;
		if ( contentPadding.bottom )
			paddingVars[ '--content-padding-bottom' ] =
				contentPadding.bottom;
		if ( contentPadding.left )
			paddingVars[ '--content-padding-left' ] =
				contentPadding.left;
	}

	const blockProps = useBlockProps( {
		className: [
			'brandy-tabs',
			`tab-orientation-${ orientation }`,
			`tab-position-${ tabPosition }`,
			`tab-align-${ tabAlignment }`,
			`active-tab-${ activeTab }`,
		].join( ' ' ),
		style: {
			'--tab-gap': tabGap,
				...( orientation === 'vertical' && tabWidth
					? { '--trigger-width': tabWidth }
					: {} ),
			...( activeTabColor
				? { '--tab-active-color': activeTabColor }
				: {} ),
			...( activeTabBg
				? { '--tab-active-bg': activeTabBg }
				: {} ),
			...( hoverTabColor
				? { '--tab-hover-color': hoverTabColor }
				: {} ),
			...( hoverTabBg
				? { '--tab-hover-bg': hoverTabBg }
				: {} ),
			...( inactiveTabBg
				? { '--tab-inactive-bg': inactiveTabBg }
				: {} ),
			...( inactiveTabColor
				? { '--tab-inactive-color': inactiveTabColor }
				: {} ),
			...paddingVars,
		},
	} );

	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'brandy-tabs__panels' },
		{ template: TEMPLATE }
	);

	return (
		<>
			<Settings
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>
			<div { ...blockProps }>
				<nav className="brandy-tabs__nav" role="tablist">
					{ children.map( ( child, i ) => (
						<Button
							key={ child.clientId }
							className={ `brandy-tabs__tab${
								i === activeTab ? ' is-active' : ''
							}` }
							onClick={ () => setActiveTab( i ) }
						>
							{ child.attributes.label || `Tab ${ i + 1 }` }
						</Button>
					) ) }
				</nav>
				<div { ...innerBlocksProps } />
			</div>
		</>
	);
}
